import type { VenueId } from "../theme/venues";

/**
 * Each venue's sustained bed, as data.
 *
 * A bed is a set of voices that never stop and never line up. `startDriftingVoices`
 * in sound.ts plays them; this file only says what they are, so the numbers can be
 * checked by a test without standing up an AudioContext (see beds.test.ts).
 *
 * TWO PROPERTIES MAKE A BED WORK, AND BOTH ARE EASY TO BREAK BY EDITING ONE LINE.
 *
 * 1. THE PERIODS MUST NOT SHARE A USEFUL COMMON FACTOR. This is the whole reason
 *    the beds stopped repeating. Every venue used to run a two-chord pad on an
 *    eleven-second timer — a 22-second cycle with two states, which a listener
 *    learns in a couple of passes. Now each voice has its OWN cycle, so what
 *    recurs is not a chord but a coincidence of six independent clocks. Brian
 *    Eno built Music for Airports this way, from tape loops cut to deliberately
 *    incommensurable lengths (23 1/2 seconds, 25 7/8, 29 15/16).
 *
 *    Round numbers throw it away: 20/30/40 is one loop with extra steps. The
 *    test asserts the closest PAIR in every bed takes at least half an hour to
 *    realign, and most take over two.
 *
 * 2. EVERY PAIR OF VOICES MUST BE CONSONANT, not just the chord as written. Any
 *    voice can sound against any other, in any combination, so there is no such
 *    thing as a passing dissonance here — a semitone in the pool is a semitone
 *    that will be held indefinitely at some point in the session. That is why
 *    several of these are pruned rather than being the plain union of the two
 *    chords they replaced: the old pads alternated colors that were fine in
 *    sequence and unusable simultaneously (Alpine's C against an F#, Pavilion's
 *    F against an F#, Library's B against a C).
 *
 *    Register matters and is not folded away: C4 against B4 is a wide major 7th
 *    and perfectly calm, while C4 against B3 is an adjacent semitone and beats.
 *
 * Gains stay very low — this sits under a card game for twenty minutes, and the
 * reverb (VENUE_ACOUSTICS) is doing most of the work of describing the room.
 */
export interface DriftVoice {
  freq: number;
  /** This voice's own cycle. It sounds for 62% of it, then rests. */
  periodSec: number;
  gain: number;
  pan?: number;
}

export const VENUE_BEDS: Record<VenueId, DriftVoice[]> = {
  /** Am9 — the union of the Cmaj7 and Am7 this replaced, so the harmony still moves between those colors. */
  coastal: [
    { freq: 220.0, periodSec: 44.0, gain: 0.014, pan: -0.2 }, // A3
    { freq: 261.6, periodSec: 38.3, gain: 0.012, pan: 0.25 }, // C4
    { freq: 329.6, periodSec: 30.1, gain: 0.011, pan: -0.3 }, // E4
    { freq: 392.0, periodSec: 29.9, gain: 0.01, pan: 0.35 }, // G4
    { freq: 493.9, periodSec: 24.9, gain: 0.009, pan: 0.1 }, // B4
  ],
  /**
   * C major pentatonic. The two chords this replaced were a D-major color and a
   * C-major one, and their union holds a C against an F# — a tritone, fine while
   * the chords alternate and grating once both can sound at once. The pentatonic
   * keeps every pair consonant at the cost of the old Lydian brightness; warmer
   * suits firelight anyway.
   */
  alpine: [
    { freq: 130.8, periodSec: 51.7, gain: 0.013, pan: 0 }, // C3
    { freq: 196.0, periodSec: 44.9, gain: 0.012, pan: -0.25 }, // G3
    { freq: 220.0, periodSec: 39.8, gain: 0.011, pan: 0.3 }, // A3
    { freq: 261.6, periodSec: 35.1, gain: 0.01, pan: -0.15 }, // C4
    { freq: 293.7, periodSec: 31.7, gain: 0.009, pan: 0.2 }, // D4
    { freq: 329.6, periodSec: 26.9, gain: 0.009, pan: -0.35 }, // E4
  ],
  /**
   * Am9, low and still. The old pair's C4 is dropped: against the B3 it is a
   * semitone, which is the maj7 doing its job when a chord is struck and a
   * beating clash when both are held indefinitely.
   */
  library: [
    { freq: 110.0, periodSec: 49.1, gain: 0.012, pan: 0 }, // A2
    { freq: 130.8, periodSec: 43.3, gain: 0.011, pan: -0.2 }, // C3
    { freq: 164.8, periodSec: 38.1, gain: 0.01, pan: 0.25 }, // E3
    { freq: 196.0, periodSec: 35.0, gain: 0.009, pan: -0.3 }, // G3
    { freq: 246.9, periodSec: 29.9, gain: 0.008, pan: 0.15 }, // B3
  ],
  /**
   * Gmaj9/13. The old pair alternated Gmaj7 with Fmaj7, whose union holds both an
   * F and an F# — a semitone across the octave, unusable when any two voices can
   * overlap. Settling on the G color and extending it upward keeps the open air.
   */
  pavilion: [
    { freq: 196.0, periodSec: 44.5, gain: 0.013, pan: -0.1 }, // G3
    { freq: 220.0, periodSec: 39.3, gain: 0.011, pan: 0.3 }, // A3
    { freq: 246.9, periodSec: 37.9, gain: 0.011, pan: -0.3 }, // B3
    { freq: 293.7, periodSec: 32.3, gain: 0.01, pan: 0.15 }, // D4
    { freq: 329.6, periodSec: 29.8, gain: 0.009, pan: -0.2 }, // E4
    { freq: 370.0, periodSec: 25.3, gain: 0.008, pan: 0.35 }, // F#4
  ],
  /**
   * Open fourths and fifths over a G, no thirds anywhere — the airless quality is
   * this venue's whole character, so the pool deliberately holds nothing that
   * could imply a major or minor chord.
   */
  observatory: [
    { freq: 98.0, periodSec: 51.7, gain: 0.015, pan: 0 }, // G2
    { freq: 146.8, periodSec: 48.2, gain: 0.013, pan: -0.25 }, // D3
    { freq: 164.8, periodSec: 41.6, gain: 0.012, pan: 0.3 }, // E3
    { freq: 196.0, periodSec: 37.9, gain: 0.011, pan: -0.15 }, // G3
    { freq: 220.0, periodSec: 33.7, gain: 0.01, pan: 0.2 }, // A3
    { freq: 293.7, periodSec: 28.9, gain: 0.01, pan: -0.35 }, // D4
  ],
  /**
   * Ab major pentatonic — the union of the old Bb and Ab colors, and the one bed
   * here that needed no pruning: every pair in it was already consonant, so the
   * smoke survives intact.
   */
  velvet: [
    { freq: 103.8, periodSec: 48.7, gain: 0.014, pan: 0 }, // Ab2
    { freq: 155.6, periodSec: 44.5, gain: 0.012, pan: -0.2 }, // Eb3
    { freq: 174.6, periodSec: 37.9, gain: 0.011, pan: 0.3 }, // F3
    { freq: 207.7, periodSec: 36.8, gain: 0.011, pan: -0.3 }, // Ab3
    { freq: 233.1, periodSec: 32.3, gain: 0.01, pan: 0.15 }, // Bb3
    { freq: 311.1, periodSec: 27.3, gain: 0.009, pan: -0.1 }, // Eb4
  ],
  /**
   * Dm7, and the periods are Eno's own ratios rather than round numbers. The
   * closest pair (25.875 and 34.125) does not line up again for 39 minutes.
   */
  olympian: [
    { freq: 146.83, periodSec: 23.5, gain: 0.014, pan: -0.25 }, // D3
    { freq: 174.61, periodSec: 29.9375, gain: 0.011, pan: 0.3 }, // F3
    { freq: 220.0, periodSec: 25.875, gain: 0.012, pan: 0.15 }, // A3
    { freq: 261.63, periodSec: 34.125, gain: 0.009, pan: -0.35 }, // C4
    { freq: 293.66, periodSec: 41.75, gain: 0.008, pan: 0.4 }, // D4
    { freq: 110.0, periodSec: 47.3, gain: 0.013, pan: 0 }, // A2, the floor
  ],
  /**
   * C# minor pentatonic, and the lowest bed here by a fourth — C#2 is under
   * every other venue's floor, which is the whole point of a room a kilometre
   * down. A pentatonic contains no semitone and no tritone at all, so this pool
   * cannot produce a pair that needs resolving however the six clocks land.
   *
   * The periods are 55.3 / 47.9 / 41.9 / 36.7 / 31.9 / 27.1 seconds. Read as
   * tenths they are 553, 479, 419, 367, 319, 271 — chosen PAIRWISE COPRIME
   * (7·79, 479, 419, 367, 11·29, 271), so the worst pair in the bed is simply
   * the two shortest, and even those do not realign for 144 minutes.
   */
  biolume: [
    { freq: 69.3, periodSec: 55.3, gain: 0.015, pan: 0 }, // C#2, the pressure
    { freq: 103.83, periodSec: 47.9, gain: 0.012, pan: -0.3 }, // G#2
    { freq: 138.59, periodSec: 41.9, gain: 0.011, pan: 0.25 }, // C#3
    { freq: 164.81, periodSec: 36.7, gain: 0.01, pan: -0.2 }, // E3
    { freq: 207.65, periodSec: 31.9, gain: 0.009, pan: 0.35 }, // G#3
    { freq: 246.94, periodSec: 27.1, gain: 0.008, pan: -0.15 }, // B3
  ],
  /**
   * F major pentatonic, warm and in the middle — the key the Sun Porch's
   * kalimba plays in, so the bed and the tines can never disagree. No floor
   * under C3: this is a small bright room, and the low end is where a bed turns
   * from "afternoon" into "evening".
   *
   * Periods in tenths are 523, 461, 409, 373, 337, 293 — all prime, so as with
   * the Midnight Zone the worst pair is simply the two shortest, and those do
   * not realign for 164 minutes.
   */
  porch: [
    { freq: 130.81, periodSec: 52.3, gain: 0.012, pan: 0 }, // C3
    { freq: 174.61, periodSec: 46.1, gain: 0.012, pan: -0.25 }, // F3
    { freq: 220.0, periodSec: 40.9, gain: 0.011, pan: 0.3 }, // A3
    { freq: 261.63, periodSec: 37.3, gain: 0.01, pan: -0.2 }, // C4
    { freq: 293.66, periodSec: 33.7, gain: 0.009, pan: 0.35 }, // D4
    { freq: 392.0, periodSec: 29.3, gain: 0.008, pan: -0.1 }, // G4
  ],
  /**
   * D minor, open and wide, with nothing in it but D, F and A — deliberately
   * NOT the Dm7 that Olympus holds, although it is the obvious chord for a
   * D-minor room.
   *
   * Hallowed Ground's organ plays D HARMONIC minor, and its raised seventh is a
   * C#. A held C natural in this pool would sit a semitone under every one of
   * those, so there is no seventh. There used to be a ninth instead, an E5 on
   * top, which was fine under a xylophone whose notes were gone in half a
   * second; under a flute pipe that HOLDS an F5 for a beat or three it was a
   * semitone beating for as long as the key was down. So the top is the D5
   * now: an organ's melody leans on D, F and A more than on anything, and those
   * are the three notes here.
   *
   * Periods in tenths are 541, 467, 431, 389, 347, 307 — all prime, so as with
   * the porch and the Midnight Zone the worst pair is simply the two shortest,
   * and those do not realign for 177 minutes.
   */
  hallowed: [
    { freq: 146.83, periodSec: 54.1, gain: 0.012, pan: 0 }, // D3
    { freq: 220.0, periodSec: 46.7, gain: 0.012, pan: -0.3 }, // A3
    { freq: 293.66, periodSec: 43.1, gain: 0.011, pan: 0.25 }, // D4
    { freq: 349.23, periodSec: 38.9, gain: 0.01, pan: -0.2 }, // F4
    { freq: 440.0, periodSec: 34.7, gain: 0.009, pan: 0.35 }, // A4
    { freq: 587.33, periodSec: 30.7, gain: 0.007, pan: -0.1 }, // D5
  ],
  /**
   * The bowed lyre's open strings droning: E and B in three octaves, with the G
   * that makes it minor. A bare fifth is the most consonant thing a pool can
   * hold, and the G sits a minor third over the E and a major third under the
   * B, so no pair here is closer than that.
   *
   * No F#, though the room's mode has one, and no D: the line wanders through
   * both, and a bed that held them would put a semitone (F# against G) or a
   * crowding whole tone (D against E) under every long note. The G3 is the
   * reason the line has no F#3.
   *
   * Periods in tenths are 569, 449, 421, 379, 331, 281 - all prime, so as with
   * the Midnight Zone and the porch the worst pair is simply the two shortest
   * (33.1s and 28.1s), and those do not realign for 155 minutes.
   */
  meadhall: [
    { freq: 82.41, periodSec: 56.9, gain: 0.013, pan: 0 }, // E2, the lyre's lowest string
    { freq: 123.47, periodSec: 44.9, gain: 0.012, pan: -0.2 }, // B2
    { freq: 164.81, periodSec: 42.1, gain: 0.011, pan: 0.25 }, // E3
    { freq: 196.0, periodSec: 37.9, gain: 0.01, pan: -0.3 }, // G3
    { freq: 246.94, periodSec: 33.1, gain: 0.009, pan: 0.2 }, // B3
    { freq: 329.63, periodSec: 28.1, gain: 0.008, pan: -0.15 }, // E4
  ],
  /**
   * C minor with no second: C, G and Eb, in the lowest register of any bed here.
   * The Eb is what makes it minor and it sits a minor third over the C and a
   * major third under the G, so no pair is closer than that - the colour comes
   * from the mode the choir sings in, not from anything the bed holds.
   *
   * There is no Db, though the room's mode is C Phrygian and the flat second is
   * its whole character. In a drifting bed there is no such thing as a passing
   * dissonance, so a Db here would be held against a C for minutes at a time;
   * the choir plays it for a second in its motif instead (see
   * `startSingularity`). There is no Ab or Bb either, and the G3 is the reason
   * the line has no Ab3: a sung Ab3 would beat against it for as long as the
   * note lasts.
   *
   * Periods in tenths are 577, 487, 433, 383, 337 - all prime, so as with the
   * Midnight Zone and the porch the worst pair is simply the two shortest
   * (38.3s and 33.7s), and those do not realign for 215 minutes.
   */
  singularity: [
    { freq: 65.41, periodSec: 57.7, gain: 0.015, pan: 0 }, // C2, the floor
    { freq: 98.0, periodSec: 48.7, gain: 0.013, pan: -0.25 }, // G2
    { freq: 130.81, periodSec: 43.3, gain: 0.012, pan: 0.3 }, // C3
    { freq: 155.56, periodSec: 38.3, gain: 0.01, pan: -0.2 }, // Eb3
    { freq: 196.0, periodSec: 33.7, gain: 0.009, pan: 0.2 }, // G3
  ],
};
